package components.project.configuration.layout.strings

import com.meistercharts.charts.pv.stringsPlanning.PvStringsPlanningLayer
import components.form.commentSection
import components.project.QuoteConfigurationTabProps
import components.project.configuration.isConfigurationEditable
import components.project.isProjectEditable
import it.neckar.commons.kotlin.js.safeGet
import it.neckar.lizergy.model.configuration.quote.builder.Inverter
import it.neckar.lizergy.model.configuration.quote.builder.InverterConfiguration
import it.neckar.lizergy.model.configuration.quote.builder.previousInverterStringCount
import it.neckar.lizergy.model.project.process.state.current
import it.neckar.lizergy.model.project.process.state.toNewProcessState
import it.neckar.react.common.*
import it.neckar.react.common.FontAwesome.faCircleCheck
import it.neckar.react.common.FontAwesome.faCircleExclamation
import it.neckar.react.common.FontAwesome.faCircleQuestion
import it.neckar.react.common.button.*
import kotlinx.css.*
import kotlinx.html.ButtonType
import kotlinx.html.classes
import kotlinx.html.id
import react.*
import react.dom.*
import router.useConfigurationIdFromUrl
import router.useDocumentTitle
import router.useLoadResolvedProjectFromUrl
import services.UiActions
import store.hooks.useLoadProcessStates
import store.hooks.useLoadProcessStatesForComponent
import store.hooks.useRequireCompanyForLoggedInUser
import styled.*

val ModuleStringsFromUrl: FC<Props> = fc("ModuleStringsFromUrl") {
  val resolvedProject = useLoadResolvedProjectFromUrl()
  val configurationId = useConfigurationIdFromUrl()
  val companyName = useRequireCompanyForLoggedInUser().simpleName
  val allProcessStatesForComponents = useLoadProcessStates()

  useDocumentTitle(companyName, "Stringplanung", allProcessStatesForComponents?.let { resolvedProject?.getDisplayName(it) }, resolvedProject?.get(configurationId)?.configurationName)

  busyIfNull(resolvedProject) { loadedProject ->
    val quoteConfiguration = loadedProject[configurationId]
    val processStatesForConfiguration = useLoadProcessStatesForComponent(quoteConfiguration.uuid)
    val currentProcessStateForConfiguration = processStatesForConfiguration?.current()?.processState ?: quoteConfiguration.processState?.currentValue?.toNewProcessState()
    val editableStatus = loadedProject.isProjectEditable().and(currentProcessStateForConfiguration.isConfigurationEditable())

    ModuleStringsOverview {
      attrs {
        this.project = loadedProject
        this.quoteConfiguration = quoteConfiguration
        this.editableStatus = editableStatus
      }
    }
  }
}

val ModuleStringsOverview: FC<QuoteConfigurationTabProps> = fc("ModuleStringsOverview") { props ->
  val project = props::project.safeGet()
  val quoteConfiguration = props::quoteConfiguration.safeGet()
  val editableStatus = props::editableStatus.safeGet()

  val moduleLayouts = quoteConfiguration.moduleLayouts

  val facilityConfiguration = quoteConfiguration.facilityConfiguration.forTheseLayouts(moduleLayouts.validElements.map { it.id })
  val inverterSelection = facilityConfiguration.inverterSelection
  val inverterConfigurations = facilityConfiguration.inverterConfigurations


  val inverterConfigurationsToSave = useState(inverterConfigurations)
  val stringConfigurationToEdit = useState<InverterConfiguration.StringConfiguration?>(null)
  val editInverterConfigurations = useState(false)

  val (downloadStringPlanBusy, setDownloadStringPlanBusy) = useState(false)


  useEffect(inverterConfigurationsToSave.value) {
    if (inverterConfigurationsToSave.value == inverterConfigurations) return@useEffect
    val updatedQuoteConfiguration = quoteConfiguration.copy(
      facilityConfiguration = facilityConfiguration.copy(
        inverterConfigurations = inverterConfigurationsToSave.value,
      )
    )
    UiActions.saveQuoteConfiguration(project, updatedQuoteConfiguration)
  }


  div("mb-5") {
    h3("mb-2") {
      +"Bemerkungen Dachflächen"
    }
    project.blueprint.roofsAnnotation?.let {
      p { +it }
    }
    commentSection(moduleLayouts)
  }

  div("my-3") {
    actionButton(
      icon = ButtonIconCentered(if (downloadStringPlanBusy) "spinner-border spinner-border-sm" else FontAwesomeIcons.Download),
      classes = "btn btn-primary ${if (quoteConfiguration == null) "disabled" else ""}",
      action = {
        check(quoteConfiguration != null) { "Configuration must not be null to create AssemblyPortfolio" }
        setDownloadStringPlanBusy(true)
        try {
          //Download the newly created quote offer snapshot
          UiActions.downloadStringPlanPdf(project.projectId, quoteConfiguration.configurationId)
        } finally {
          setDownloadStringPlanBusy(false)
        }
      }) {
      b("ms-2") {
        +"Stringplan herunterladen"
      }
    }
  }

  styledDiv {
    attrs {
      id = "roofAccordion"
      classes = setOf("accordion")
    }
    css {
      position = Position.sticky
      top = 80.px
      zIndex = 10
    }

    div("accordion-item") {
      h2("accordion-header mt-0") {
        button(classes = "accordion-button", type = ButtonType.button) {
          attrs["aria-expanded"] = "true"
          attrs["data-bs-toggle"] = "collapse"
          attrs["data-bs-target"] = "#collapseOne"
          attrs["aria-controls"] = "#collapseOne"

          +"Verschaltung"

          if (inverterConfigurationsToSave.value.all { it.isValid() } && inverterConfigurationsToSave.value.sumOf { it.numberOfPlannedModules } == moduleLayouts.totalNumberOfModules) {
            span("text-primary ms-2") {
              faCircleCheck()
            }
          }

          val stringConfiguration = stringConfigurationToEdit.value
          val inverterConfigurations = inverterConfigurationsToSave.value
          if (stringConfiguration != null) {
            val inverterConfiguration = inverterConfigurations.firstOrNull { it.contains(stringConfiguration) }
            if (inverterConfiguration != null) {
              val inverterIndex = inverterConfigurations.indexOfFirst { it.id == inverterConfiguration.id }
              val inverter = inverterConfiguration.inverter

              val mpptInputConfiguration = inverterConfiguration.relevantMpptConfigurations.first { it.contains(stringConfiguration) }
              val mpptInputIndex = inverterConfiguration.relevantMpptConfigurations.indexOfFirst { it.id == mpptInputConfiguration.id }
              val stringIndex = mpptInputConfiguration.relevantStringConfigurations.indexOfFirst { it.id == stringConfiguration.id }

              val optimalModuleCount = stringConfiguration.optimalModuleCount
              val totalModuleCount = stringConfiguration.totalModuleCount

              val previousInverterStringCount = inverterConfigurationsToSave.value.previousInverterStringCount(inverterIndex)
              val previousMpptInputStringCount = inverterConfiguration.previousMpptInputStringCount(mpptInputIndex)
              val globalStringIndex = previousInverterStringCount + previousMpptInputStringCount + stringIndex
              val stringColor = PvStringsPlanningLayer.Configuration.defaultStringColors.valueAt(globalStringIndex)
              val stringLabelBoxTextColor = PvStringsPlanningLayer.Configuration.defaultStringLabelBoxTextColors.valueAt(globalStringIndex)

              span("ms-4") {
                b("me-2") {
                  +"${inverterIndex + 1}"
                }

                span {
                  b("me-2") {
                    +inverter.description
                  }
                  if (inverter is Inverter && inverterSelection[inverter] > 1) +"#${inverterConfiguration.inverterIndex + 1}"
                }
              }

              span("ms-4") {
                b("me-2") {
                  +"${'A' + mpptInputConfiguration.inputIndex}"
                }

                span {
                  b("me-2") {
                    +"MPP-Eingang"
                  }
                  +"${mpptInputConfiguration.inputIndex + 1}"
                }
              }

              span("ms-4") {
                b("me-2") { +"${stringIndex + 1}" }

                styledSpan {
                  attrs {
                    classes = setOf("badge", "me-3")
                  }
                  css {
                    color = Color(stringLabelBoxTextColor.web)
                    backgroundColor = Color(stringColor.web)
                  }

                  +"String #${globalStringIndex + 1}"
                }

                span {
                  span("me-2") {
                    if (totalModuleCount == optimalModuleCount) {
                      addClass("text-success")
                      faCircleCheck()
                    } else if (optimalModuleCount != null) {
                      if (optimalModuleCount > totalModuleCount) {
                        addClass("text-warning")
                        faCircleExclamation()
                      } else if (totalModuleCount > optimalModuleCount) {
                        addClass("text-danger")
                        faCircleExclamation()
                      }
                    } else {
                      addClass("text-danger")
                      faCircleQuestion()
                    }
                  }

                  span("form-text") { +"$totalModuleCount / $optimalModuleCount Module" }
                }
              }
            }
          }
        }
      }

      div("accordion-collapse collapse show") {
        attrs["id"] = "collapseOne"
        attrs["data-bs-parent"] = "#roofAccordion"

        div("accordion-body") {
          styledDiv {
            css {
              overflowX = Overflow.auto
            }

            if (editInverterConfigurations.value) {
              editFacilityConfiguration(moduleLayouts.validElements, inverterSelection, inverterConfigurationsToSave, editInverterConfigurations, editableStatus)
            } else {
              facilityConfigurationOverview(moduleLayouts.validElements, inverterSelection, stringConfigurationToEdit, inverterConfigurationsToSave, editInverterConfigurations, editableStatus)
            }
          }
        }
      }
    }
  }

  moduleLayouts.fastForEach { moduleLayout ->
    EditModuleStrings {
      attrs {
        this.project = project
        this.inverterConfigurationsToSave = inverterConfigurationsToSave
        this.moduleLayout = moduleLayout
        this.stringConfigurationToEdit = stringConfigurationToEdit
        this.editableStatus = editableStatus.and(editInverterConfigurations.value.not())
      }
    }
  }
}
